import { pushEcommerceEvent } from './dataLayer';
import { readListContext, setListContext } from './listContext';

/**
 * view_item_list and select_item.
 *
 * Both read the same data-tracking-item attributes that the product card
 * renders on the server, which keeps the two events consistent. Lists that are
 * re-rendered by the faceted search (the api returns server rendered html) are
 * picked up by the mutation observer and tracked again with the new products.
 */

const trackedLists = new WeakMap();

const readItems = (container) =>
    Array.from(container.querySelectorAll('[data-tracking-item]'))
        .map((element, index) => {
            try {
                return { ...JSON.parse(element.dataset.trackingItem), index };
            } catch (e) {
                return null;
            }
        })
        .filter(Boolean);

const listSignature = (items) => items.map((item) => item.item_id).join('|');

const trackList = (container) => {
    const items = readItems(container);
    if (items.length === 0) {
        return;
    }

    // The same list is rendered again on every filter change - only track it
    // when the products actually differ.
    const signature = listSignature(items);
    if (trackedLists.get(container) === signature) {
        return;
    }
    trackedLists.set(container, signature);

    const listId = container.dataset.trackingListId;
    const listName = container.dataset.trackingListName;

    pushEcommerceEvent('view_item_list', {
        item_list_id: listId,
        item_list_name: listName,
        items: items.map((item) => ({
            ...item,
            item_list_id: listId,
            item_list_name: listName,
        })),
    });
};

const trackAllLists = () => {
    Array.from(document.querySelectorAll('[data-tracking-list-id]')).forEach(
        (container) => trackList(container)
    );
};

const onClick = (event) => {
    const holder = event.target.closest('[data-tracking-item]');
    if (!holder) {
        return;
    }

    let item;
    try {
        item = JSON.parse(holder.dataset.trackingItem);
    } catch (e) {
        return;
    }

    const { listId, listName } = readListContext(holder);

    // Remember where the product came from, so an add to cart that follows can
    // be attributed to the same list.
    setListContext(item.item_id, listId, listName);

    // A click on the buy button or the quantity field is not a product click.
    if (event.target.closest('[data-tracking-ignore-select], input, button')) {
        return;
    }

    pushEcommerceEvent('select_item', {
        item_list_id: listId,
        item_list_name: listName,
        items: [{ ...item, item_list_id: listId, item_list_name: listName }],
    });
};

export const initProductListTracking = () => {
    if (!document.querySelector('[data-tracking-list-id], [data-tracking-item]')) {
        return;
    }

    trackAllLists();
    document.addEventListener('click', onClick, true);

    // The faceted search replaces the product list html without a page load.
    const target = document.getElementById('search-result') || document.body;
    let timer = null;
    const observer = new MutationObserver(() => {
        window.clearTimeout(timer);
        timer = window.setTimeout(trackAllLists, 250);
    });
    observer.observe(target, { childList: true, subtree: true });
};
